import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { InputSelector } from '../../UI/InputSelector';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { MultiSelectDropdown } from '../../UI/MultiSelectDropdown';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { getAllDesksApi } from '../../../utils/api/Admin/desk/getAllDesksApi';
import { getAdmins } from '../../../utils/api/Admin/getAdminsApi';
import { getTelegramActionsApi } from '../../../utils/api/Notification/getTelegramActionsApi';
import styles from '../../../styles/components/Modal/TelegramNotifications/TelegramDestinationModal.module.css';

const translations = {
    ru: {
        createTitle: 'Добавить Telegram направление',
        editTitle: 'Редактировать Telegram направление',
        save: 'Сохранить',
        cancel: 'Отмена',
        name: 'Название',
        chatId: 'Chat ID',
        routingType: 'Тип маршрута',
        manager: 'Менеджер',
        desk: 'Деск',
        actions: 'События',
        enabled: 'Активно',
        disabled: 'Неактивно',
        loading: 'Загрузка данных...',
        routingOptions: {
            global: 'Глобальный',
            manager: 'Менеджер',
            desk: 'Деск',
        },
        allActionsHint: 'Пустой список означает все события.',
        errors: {
            name: 'Название обязательно.',
            chat_id: 'Chat ID обязателен.',
        },
    },
    en: {
        createTitle: 'Add Telegram Destination',
        editTitle: 'Edit Telegram Destination',
        save: 'Save',
        cancel: 'Cancel',
        name: 'Name',
        chatId: 'Chat ID',
        routingType: 'Routing Type',
        manager: 'Manager',
        desk: 'Desk',
        actions: 'Actions',
        enabled: 'Enabled',
        disabled: 'Disabled',
        loading: 'Loading data...',
        routingOptions: {
            global: 'Global',
            manager: 'Manager',
            desk: 'Desk',
        },
        allActionsHint: 'Empty action list means all actions.',
        errors: {
            name: 'Name is required.',
            chat_id: 'Chat ID is required.',
        },
    },
};

const getRoutingType = (initialData) => {
    if (initialData?.manager_id) {
        return 'manager';
    }

    if (initialData?.desk_id) {
        return 'desk';
    }

    return 'global';
};

const getTelegramActionItems = (response) => {
    if (Array.isArray(response?.items)) {
        return response.items;
    }

    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    if (Array.isArray(response)) {
        return response;
    }

    return [];
};

const withSelectedOption = (options, selectedValue, fallbackLabel) => {
    if (!selectedValue || options.some((option) => String(option.value) === String(selectedValue))) {
        return options;
    }

    return [
        {
            value: String(selectedValue),
            label: `${fallbackLabel} #${selectedValue}`,
            description: '',
        },
        ...options,
    ];
};

const withSelectedOptions = (options, selectedValues) => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
        return options;
    }

    const optionValues = new Set(options.map((option) => String(option.value)));
    const missingOptions = selectedValues
        .filter((value) => value && !optionValues.has(String(value)))
        .map((value) => ({
            value,
            label: String(value),
        }));

    if (missingOptions.length === 0) {
        return options;
    }

    return [...missingOptions, ...options];
};

export const TelegramDestinationModal = ({
    open,
    onClose,
    onSave,
    initialData = null,
    isSubmitting = false,
}) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';
    const isEditing = Boolean(initialData);
    const modalRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        chat_id: '',
        is_enabled: true,
        routing_type: 'global',
        manager_id: '',
        desk_id: '',
        actions: [],
    });
    const [managerOptions, setManagerOptions] = useState([]);
    const [deskOptions, setDeskOptions] = useState([]);
    const [actionOptions, setActionOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [errors, setErrors] = useState({});
    const [focused, setFocused] = useState({});

    const managerOptionsForSelect = useMemo(
        () => withSelectedOption(managerOptions, formData.manager_id, t.manager),
        [managerOptions, formData.manager_id, t.manager]
    );
    const deskOptionsForSelect = useMemo(
        () => withSelectedOption(deskOptions, formData.desk_id, t.desk),
        [deskOptions, formData.desk_id, t.desk]
    );
    const actionOptionsForSelect = useMemo(
        () => withSelectedOptions(actionOptions, formData.actions),
        [actionOptions, formData.actions]
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setFormData({
            name: initialData?.name || '',
            chat_id: initialData?.chat_id || '',
            is_enabled: initialData?.is_enabled !== undefined ? Boolean(initialData.is_enabled) : true,
            routing_type: getRoutingType(initialData),
            manager_id: initialData?.manager_id ? String(initialData.manager_id) : '',
            desk_id: initialData?.desk_id ? String(initialData.desk_id) : '',
            actions: Array.isArray(initialData?.actions) ? initialData.actions : [],
        });
        setErrors({});
        setFocused({});
    }, [initialData, open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const fetchOptions = async () => {
            setLoadingOptions(true);

            try {
                const [desksResult, adminsResult, actionsResult] = await Promise.all([
                    getAllDesksApi(1, 100),
                    getAdmins(1, 100),
                    getTelegramActionsApi(),
                ]);

                setDeskOptions((desksResult.data || []).map((desk) => ({
                    value: String(desk.id),
                    label: desk.name,
                    description: desk.description || '',
                })));

                setManagerOptions((adminsResult.data || []).map((admin) => ({
                    value: String(admin.id),
                    label: admin.first_name ? `${admin.first_name} ${admin.last_name || ''}`.trim() : admin.email,
                    description: admin.role?.name || '',
                })));

                setActionOptions(getTelegramActionItems(actionsResult).map((action) => ({
                    value: action,
                    label: action,
                })));
            } catch (error) {
                setErrors((prev) => ({
                    ...prev,
                    general: error.message || 'Failed to load Telegram modal data',
                }));
            } finally {
                setLoadingOptions(false);
            }
        };

        fetchOptions();
    }, [open]);

    const handleChange = (key, value) => {
        setFormData((prev) => {
            const next = { ...prev, [key]: value };

            if (key === 'routing_type') {
                if (value === 'global') {
                    next.manager_id = '';
                    next.desk_id = '';
                }

                if (value === 'manager') {
                    next.desk_id = '';
                }

                if (value === 'desk') {
                    next.manager_id = '';
                }
            }

            return next;
        });

        setErrors((prev) => ({ ...prev, [key]: '', general: prev.general }));
    };

    const handleFocus = (key) => {
        setFocused((prev) => ({ ...prev, [key]: true }));
        setErrors((prev) => ({ ...prev, [key]: '' }));
    };

    const handleBlur = (key) => {
        setFocused((prev) => ({ ...prev, [key]: false }));
    };

    const handleEnabledToggle = () => {
        setFormData((prev) => ({
            ...prev,
            is_enabled: !prev.is_enabled,
        }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!formData.name.trim()) {
            nextErrors.name = t.errors.name;
        }

        if (!formData.chat_id.trim()) {
            nextErrors.chat_id = t.errors.chat_id;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) {
            return;
        }

        onSave({
            name: formData.name.trim(),
            chat_id: formData.chat_id.trim(),
            is_enabled: Boolean(formData.is_enabled),
            manager_id: formData.routing_type === 'manager' && formData.manager_id ? Number(formData.manager_id) : null,
            desk_id: formData.routing_type === 'desk' && formData.desk_id ? Number(formData.desk_id) : null,
            actions: Array.isArray(formData.actions) ? formData.actions : [],
        }, initialData?.id || null);
    };

    if (!open) {
        return null;
    }

    return (
        <div className={styles.modal + (isDark ? ' ' + styles.darkModal : '')}>
            <div className={styles.filtersContainer + (isDark ? ' ' + styles.darkFiltersContainer : '')} ref={modalRef}>
                <div className={styles.modalLabel + (isDark ? ' ' + styles.darkModalLabel : '')}>
                    <p className={isDark ? styles.darkModalLabelText : ''}>{isEditing ? t.editTitle : t.createTitle}</p>
                    <button type="button" onClick={onClose} className={styles.closeButton + (isDark ? ' ' + styles.darkCloseButton : '')}>
                        <CloseIcon />
                    </button>
                </div>

                <div className={styles.modalContent}>
                    <div className={styles.section}>
                        <div className={styles.inputRow}>
                            <label className={isDark ? styles.darkLabel : ''}>{t.name}:</label>
                            <InputSelector
                                filterName="name"
                                selectedValue={formData.name}
                                handleFilterChange={handleChange}
                                onFocus={() => handleFocus('name')}
                                onBlur={() => handleBlur('name')}
                                hasError={Boolean(errors.name)}
                                errorMessage={errors.name}
                                isFocused={focused.name}
                            />
                        </div>

                        <div className={styles.inputRow}>
                            <label className={isDark ? styles.darkLabel : ''}>{t.chatId}:</label>
                            <InputSelector
                                filterName="chat_id"
                                selectedValue={formData.chat_id}
                                handleFilterChange={handleChange}
                                onFocus={() => handleFocus('chat_id')}
                                onBlur={() => handleBlur('chat_id')}
                                hasError={Boolean(errors.chat_id)}
                                errorMessage={errors.chat_id}
                                isFocused={focused.chat_id}
                            />
                        </div>

                        <div className={styles.inputRow}>
                            <label className={isDark ? styles.darkLabel : ''}>{t.routingType}:</label>
                            <DropdownSelector
                                filterName="routing_type"
                                selectedValue={formData.routing_type}
                                handleFilterChange={handleChange}
                                options={[
                                    { value: 'global', label: t.routingOptions.global },
                                    { value: 'manager', label: t.routingOptions.manager },
                                    { value: 'desk', label: t.routingOptions.desk },
                                ]}
                            />
                        </div>

                        {formData.routing_type === 'manager' && (
                            <div className={styles.inputRow}>
                                <label className={isDark ? styles.darkLabel : ''}>{t.manager}:</label>
                                <DropdownSelector
                                    filterName="manager_id"
                                    selectedValue={formData.manager_id}
                                    handleFilterChange={handleChange}
                                    options={managerOptionsForSelect}
                                    disabled={loadingOptions}
                                    shouldAutoClearOnMissingOptions={false}
                                />
                            </div>
                        )}

                        {formData.routing_type === 'desk' && (
                            <div className={styles.inputRow}>
                                <label className={isDark ? styles.darkLabel : ''}>{t.desk}:</label>
                                <DropdownSelector
                                    filterName="desk_id"
                                    selectedValue={formData.desk_id}
                                    handleFilterChange={handleChange}
                                    options={deskOptionsForSelect}
                                    disabled={loadingOptions}
                                    shouldAutoClearOnMissingOptions={false}
                                />
                            </div>
                        )}

                        <div className={styles.inputRow}>
                            <label className={isDark ? styles.darkLabel : ''}>{t.actions}:</label>
                            <MultiSelectDropdown
                                filterName="actions"
                                selectedValue={formData.actions}
                                handleFilterChange={handleChange}
                                options={actionOptionsForSelect}
                                disabled={loadingOptions}
                                shouldAutoClearOnMissingOptions={false}
                            />
                        </div>

                        <div className={styles.helperText + (isDark ? ' ' + styles.darkHelperText : '')}>{t.allActionsHint}</div>

                        <div className={styles.inputRow}>
                            <label className={isDark ? styles.darkLabel : ''}>{t.enabled}:</label>
                            <button
                                type="button"
                                onClick={handleEnabledToggle}
                                className={styles.switchButton + ' ' + (formData.is_enabled ? styles.isActive : styles.isInactive) + (isDark ? ' ' + styles.darkSwitchButton : '')}
                            >
                                {formData.is_enabled ? t.enabled : t.disabled}
                            </button>
                        </div>

                        {loadingOptions && <div className={styles.helperText + (isDark ? ' ' + styles.darkHelperText : '')}>{t.loading}</div>}
                        {errors.general && <div className={styles.generalError}>{errors.general}</div>}
                    </div>
                </div>

                <div className={styles.modalFooter + (isDark ? ' ' + styles.darkModalFooter : '')}>
                    <button type="button" className={styles.cancelButton} onClick={onClose}>
                        {t.cancel}
                    </button>
                    <button type="button" className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
                        {t.save}
                    </button>
                </div>
            </div>
        </div>
    );
};
